import styled from "styled-components";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import BackgroundBlock from "../BackgroundBlock";
import { useLocation } from 'react-router-dom';
import {HomeHeader} from "../HomeHeader";
import GroupHeader from "../GroupHeader";
import GroupCard from "./GroupCard";
import AdminDataService from "../../../services/AdminDataService";
import { usePrivacy } from "../../../contexts/PrivacyContext";

const GroupViewer = () => {
    const location = useLocation();
    const { course, groupData: initialGroupData } = location.state || {};
    const { maskCourseName } = usePrivacy();
    const sortingCriteria = { "inf121_default": "Default Order", "name": "Group Name", "total_event": "Total Events", "last_two_day_event": "Last 2 Day Events" };

    const [sortOrder, setSortOrder] = useState(() => {
        return localStorage.getItem("sortOrder") || "asc";
    });
    const [sortCriteria, setSortCriteria] = useState(() => {
        return localStorage.getItem("sortCriteria") || "inf121_default";
    });

    const [groups, setGroups] = useState([]);
    const [allGroups, setAllGroups] = useState([]); // Store all groups (unfiltered)
    const [loading, setLoading] = useState(!initialGroupData);
    const [selectedStudent, setSelectedStudent] = useState(null);

    // Sorting functions
    const sortedByName = (data) => {
        const sortedData = [...data];
        if (sortOrder === "asc") {
            return sortedData.sort((a, b) => a.groupName.localeCompare(b.groupName));
        } else {
            return sortedData.sort((a, b) => b.groupName.localeCompare(a.groupName));
        }
    };

    const sortedByTotalEvents = (data) => {
        const sortedData = [...data];
        if (sortOrder === "asc") {
            return sortedData.sort((a, b) => a.numEvent - b.numEvent);
        } else {
            return sortedData.sort((a, b) => b.numEvent - a.numEvent);
        }
    };

    const sortedByLastTwoDayEvents = (data) => {
        const sortedData = [...data];
        if (sortOrder === "asc") {
            return sortedData.sort((a, b) => (Number(a.numLastTwoDayEvent) || 0) - (Number(b.numLastTwoDayEvent) || 0));
        } else {
            return sortedData.sort((a, b) => (Number(b.numLastTwoDayEvent) || 0) - (Number(a.numLastTwoDayEvent) || 0));
        }
    };

    const sortedByInf121Default = (data) => {
        const sortedData = [...data];
        return sortedData.sort((a, b) => {
            const discussionMatchA = a.groupName.match(/Discussion (\d+)/);
            const discussionMatchB = b.groupName.match(/Discussion (\d+)/);
            const discussionNumberA = discussionMatchA ? parseInt(discussionMatchA[1]) : 0;
            const discussionNumberB = discussionMatchB ? parseInt(discussionMatchB[1]) : 0;

            const groupMatchA = a.groupName.match(/Group (\d+)$/);
            const groupMatchB = b.groupName.match(/Group (\d+)$/);
            const groupNumberA = groupMatchA ? parseInt(groupMatchA[1]) : 0;
            const groupNumberB = groupMatchB ? parseInt(groupMatchB[1]) : 0;

            if (discussionNumberA === discussionNumberB) {
                return groupNumberA - groupNumberB;
            } else {
                return discussionNumberA - discussionNumberB;
            }
        });
    };

    // Initial loading of data
    useEffect(() => {
        const loadGroups = async () => {
            if (!course || !course.id) return;
            setLoading(true);
            try {
                // If we have initial data, use it, otherwise fetch from service
                if (initialGroupData && initialGroupData.length > 0) {
                    applySort(initialGroupData);
                } else {
                    // Fetch course groups with events data
                    const groupsData = await AdminDataService.getCourseGroups(course.id, true);
                    applySort(groupsData);
                }
            } catch (error) {
                console.error("Error loading groups:", error);
            } finally {
                setLoading(false);
            }
        };

        loadGroups();
    }, [course]);

    // Apply sorting when sort criteria or order changes
    useEffect(() => {
        if (!allGroups.length) return;

        const applyNewSort = () => {
            const groupsCopy = [...allGroups];
            let sortedGroups;

            if (sortCriteria === "name") {
                sortedGroups = sortedByName(groupsCopy);
            } else if (sortCriteria === "total_event") {
                sortedGroups = sortedByTotalEvents(groupsCopy);
            } else if (sortCriteria === "last_two_day_event") {
                sortedGroups = sortedByLastTwoDayEvents(groupsCopy);
            } else if (sortCriteria === "inf121_default") {
                sortedGroups = sortedByInf121Default(groupsCopy);
            } else {
                sortedGroups = groupsCopy;
            }

            setAllGroups(sortedGroups);
            applyFilter(sortedGroups);
        };

        applyNewSort();
        localStorage.setItem("sortOrder", sortOrder);
        localStorage.setItem("sortCriteria", sortCriteria);
    }, [sortCriteria, sortOrder]);

    // Helper function to apply sorting to new data
    const applySort = (data) => {
        if (!data || !data.length) {
            setGroups([]);
            setAllGroups([]);
            return;
        }

        let sortedGroups;
        if (sortCriteria === "name") {
            sortedGroups = sortedByName(data);
        } else if (sortCriteria === "total_event") {
            sortedGroups = sortedByTotalEvents(data);
        } else if (sortCriteria === "last_two_day_event") {
            sortedGroups = sortedByLastTwoDayEvents(data);
        } else if (sortCriteria === "inf121_default") {
            sortedGroups = sortedByInf121Default(data);
        } else {
            sortedGroups = [...data];
        }

        setAllGroups(sortedGroups);
        applyFilter(sortedGroups);
    };

    // Filter groups based on selected student
    const applyFilter = useCallback((groupsToFilter) => {
        if (!selectedStudent) {
            setGroups(groupsToFilter);
            return;
        }

        const filtered = groupsToFilter.filter(group => {
            if (!group.members || !Array.isArray(group.members)) return false;
            return group.members.some(member => 
                member.email === selectedStudent.email
            );
        });
        setGroups(filtered);
    }, [selectedStudent]);

    // Extract unique students from all groups (memoized)
    const allStudents = useMemo(() => {
        const studentMap = new Map();
        allGroups.forEach(group => {
            if (group.members && Array.isArray(group.members)) {
                group.members.forEach(member => {
                    if (member.email && member.name) {
                        // Use email as unique key since a student can be in multiple groups
                        if (!studentMap.has(member.email)) {
                            studentMap.set(member.email, {
                                email: member.email,
                                name: member.name,
                                color: member.color,
                                initial: member.initial
                            });
                        }
                    }
                });
            }
        });
        return Array.from(studentMap.values()).sort((a, b) => 
            a.name.localeCompare(b.name)
        );
    }, [allGroups]);

    // Apply filter when selected student or allGroups changes
    useEffect(() => {
        if (allGroups.length > 0) {
            applyFilter(allGroups);
        }
    }, [selectedStudent, allGroups, applyFilter]);

    // Handle student selection
    const handleStudentSelect = (student) => {
        setSelectedStudent(student);
    };

    // Handle student clear
    const handleStudentClear = () => {
        setSelectedStudent(null);
    };

    return (
        <>
            <HomeHeader text="Group Viewer" link="/admin/home" showPrivacyButton />
            <PageWrapper>
                <BackgroundBlock>
                    <GroupHeader
                        text={maskCourseName(course?.name || "Unknown Course", course?.id)}
                        sortingCriteria={sortingCriteria}
                        sortOrder={sortOrder}
                        setSortOrder={setSortOrder}
                        sortCriteria={sortCriteria}
                        setSortCriteria={setSortCriteria}
                        students={allStudents}
                        selectedStudent={selectedStudent}
                        onStudentSelect={handleStudentSelect}
                        onStudentClear={handleStudentClear}
                    />

                    {loading ? (
                        <LoadingMessage>Loading groups...</LoadingMessage>
                    ) : (
                        <GroupsGrid>
                            {groups.length > 0 ? (
                                groups.map((group, index) => (
                                    <GroupCard
                                        key={group.id}
                                        groupIndex={index}
                                        course={course}
                                        groups={groups}
                                    />
                                ))
                            ) : (
                                <NoGroupsMessage>No groups available for this course.</NoGroupsMessage>
                            )}
                        </GroupsGrid>
                    )}
                </BackgroundBlock>
            </PageWrapper>
        </>
    );
};

const PageWrapper = styled.div`
    background-color: #f8fafc;
    display: flex;
    flex-direction: column;
`;

const GroupsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
    gap: 20px;
    width: 100%;
    margin: 0 20px;
`;

const LoadingMessage = styled.div`
    width: 100%;
    padding: 20px;
    text-align: center;
    font-size: 16px;
    color: #666;
`;

const NoGroupsMessage = styled.div`
    width: 100%;
    padding: 20px;
    text-align: center;
    font-size: 16px;
    color: #666;
    background-color: #f9f9f9;
    border-radius: 8px;
`;

export default GroupViewer;
